import { Address, Contact } from '@akin-home/amplify';
import {
  append,
  removeByIndex,
  setByIndex,
  sortByStringAttribute,
} from '@akin-home/arrays';
import produce from 'immer';
import { ContactModel, PhoneNumber, PhoneTypes } from '../types';

export const createContactModel = (
  input?: Partial<ContactModel>
): ContactModel => ({
  id: '',
  habitatID: '',
  name: '',
  tags: [],
  phoneNumbers: [],
  emailAddresses: [],
  notes: [],
  contactColor: '',
  imageUrl: '',
  ...input,

  removePhoneNumber,
  addPhoneNumber,
  updatePhoneNumber,
  hasPhoneNumber,
});

function addPhoneNumber(this: ContactModel, phoneNumber?: PhoneNumber) {
  return {
    ...this,
    phoneNumbers: [...this.phoneNumbers, phoneNumber || createPhoneNumber()],
  };
}

function removePhoneNumber(this: ContactModel, idx: number) {
  return {
    ...this,
    phoneNumbers: this.phoneNumbers.filter((_, i) => i !== idx),
  };
}

function updatePhoneNumber(
  this: ContactModel,
  idx: number,
  phoneNumber: PhoneNumber
) {
  return {
    ...this,
    phoneNumbers: this.phoneNumbers.map((phone, i) =>
      i === idx ? phoneNumber : phone
    ),
  };
}

export const createPhoneNumber = (
  input?: Partial<PhoneNumber>
): PhoneNumber => ({
  number: '',
  type: PhoneTypes.Personal,
  ...input,
});

export function hasPhoneNumber(this: ContactModel) {
  return this.phoneNumbers.some((phone_number) => phone_number.number.length);
}

/** Filter contacts by some given text */
export const filterContactsByText = <T extends Contact | ContactModel>(
  contacts: T[],
  text?: string
): T[] => {
  if (!text || text.length === 0) return contacts;

  const lowerCaseText = text.toLowerCase();

  return contacts.filter((contact) =>
    contact.name?.toLowerCase().includes(lowerCaseText)
  );
};

export const canCallContact = (contact: ContactModel) => {
  return contact.phoneNumbers.some(
    (phone_number) => phone_number.number.length
  );
};

export const findFirstEmailAddress = (contact?: ContactModel) => {
  return contact?.emailAddresses.find((address) => address.length);
};

export const findFirstPhoneNumber = (contact?: ContactModel) => {
  return contact?.phoneNumbers.find((phone_number) => phone_number.number)
    ?.number;
};

export const sortContactsByName = sortByStringAttribute<ContactModel, string>(
  'name'
);

export const createAddress = (defaults?: Partial<Address>): Address => ({
  __typename: 'Address',
  type: 'home',
  ...defaults,
});

export const contactHasLinkedUser = (contact: ContactModel) =>
  !!contact.user_id;

export const setName = (name: string) => (contact: ContactModel) => ({
  ...contact,
  name,
});

export const addContactPhoneNumber = (contact: ContactModel): ContactModel => ({
  ...contact,
  phoneNumbers: append(contact.phoneNumbers, createPhoneNumber()),
});

export const setPhoneNumber =
  (phone_number: PhoneNumber, idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    phoneNumbers: setByIndex(contact.phoneNumbers, idx, phone_number),
  });

export const removeContactPhoneNumber =
  (idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    phoneNumbers: removeByIndex(contact.phoneNumbers, idx),
  });

export const addEmailAddress = (contact: ContactModel): ContactModel => ({
  ...contact,
  emailAddresses: append(contact.emailAddresses, ''),
});

export const removeEmailAddress =
  (idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    emailAddresses: removeByIndex(contact.emailAddresses, idx),
  });

export const setContactEmail =
  (email: string, idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    emailAddresses: setByIndex(contact.emailAddresses, idx, email),
  });

export const addNote = (contact: ContactModel): ContactModel => ({
  ...contact,
  notes: append(contact.notes, ''),
});

export const removeNote =
  (idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    notes: removeByIndex(contact.notes, idx),
  });

export const setNote =
  (note: string, idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    notes: setByIndex(contact.notes, idx, note),
  });

export const addAddressToContact = produce((contact: ContactModel) => {
  const address = createAddress();

  if (contact.addresses) {
    contact.addresses.push(address);
  } else {
    contact.addresses = [address];
  }
});

export const setAddress =
  (address: Address, idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    addresses: setByIndex(contact.addresses, idx, address),
  });

export const removeAddress =
  (idx: number) =>
  (contact: ContactModel): ContactModel => ({
    ...contact,
    addresses: removeByIndex(contact.addresses || [], idx),
  });

export const sortByName = (contacts?: ContactModel[]) =>
  contacts?.sort((contact1, contact2) => {
    const value_a = contact1.name.toLowerCase();
    const value_b = contact2.name.toLowerCase();

    return value_a < value_b ? -1 : value_a > value_b ? 1 : 0;
  });
